const API_URL = process.env.REACT_APP_API_URL || '';

class ProductStrategyCapability {
  constructor(planStateSetters, planGetters, savePlan) {
    this.setters = planStateSetters;
    this.getters = planGetters;
    this.savePlan = savePlan;
  }

  async updateProductStrategy(updates, planId) {
    if (!planId) {
      console.error("❌ No planId available for Product Strategy update");
      return { success: false, message: "No plan ID available" };
    }

    try {
      const token = localStorage.getItem('token');
      
      console.log(`📝 ProductStrategyCapability: Updating Product Strategy for plan ${planId}:`, Object.keys(updates));

      window.dispatchEvent(new CustomEvent('productStrategyUpdating', {
        detail: { updatingFields: Object.keys(updates), planId }
      }));
      
      const response = await fetch(
        `${API_URL}/api/product-strategy/plan/${planId}/update`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updates)
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }

      console.log('✅ Product Strategy update response:', data);

      const updatedFields = data.fieldsUpdated || Object.keys(updates);
      window.dispatchEvent(new CustomEvent('productStrategyUpdated', {
        detail: { updatedFields, planId }
      }));

      return {
        success: true,
        message: `Updated ${updatedFields.join(', ')}`,
        updatedFields,
        version: data.version
      };
    } catch (error) {
      console.error("❌ Error updating Product Strategy:", error);
      window.dispatchEvent(new CustomEvent('productStrategyUpdated', {
        detail: { updatedFields: Object.keys(updates), planId, error: true }
      }));
      return { 
        success: false, 
        message: error.message 
      };
    }
  }

  async handleUpdateDesignVision(data, action = null, context = {}) {
    console.log("🎯 ProductStrategyCapability: Updating design vision", data);
    
    const designVision = data.designVision || data.design_vision || data.content || data.value || data.text;
    const planId = context.planId || this.getters?.currentPlanId || (typeof this.getters?.getCurrentPlanId === 'function' ? this.getters.getCurrentPlanId() : null);
    
    if (!designVision) {
      console.error("❌ No design vision content provided");
      return { success: false, message: "No design vision content provided" };
    }

    return this.updateProductStrategy({ designVision }, planId);
  }

  async handleUpdateStrategicThemes(data, action = null, context = {}) {
    console.log("🎯 ProductStrategyCapability: Updating strategic themes", data);
    
    const strategicThemes = data.strategicThemes || data.strategic_themes || data.themes || data.content;
    const planId = context.planId || this.getters?.currentPlanId || (typeof this.getters?.getCurrentPlanId === 'function' ? this.getters.getCurrentPlanId() : null);
    
    if (!strategicThemes) {
      console.error("❌ No strategic themes provided");
      return { success: false, message: "No strategic themes provided" };
    }

    const updates = { strategicThemes };
    if (data._itemIndex !== undefined) updates._itemIndex = data._itemIndex;
    return this.updateProductStrategy(updates, planId);
  }

  async handleUpdateOpportunityAreas(data, action = null, context = {}) {
    console.log("🎯 ProductStrategyCapability: Updating opportunity areas", data);
    
    const opportunityAreas = data.opportunityAreas || data.opportunity_areas || data.opportunities || data.content;
    const planId = context.planId || this.getters?.currentPlanId || (typeof this.getters?.getCurrentPlanId === 'function' ? this.getters.getCurrentPlanId() : null);
    
    if (!opportunityAreas) {
      console.error("❌ No opportunity areas provided");
      return { success: false, message: "No opportunity areas provided" };
    }

    const updates = { opportunityAreas };
    if (data._itemIndex !== undefined) {
      updates._itemIndex = data._itemIndex;
    } else if (!Array.isArray(opportunityAreas)) {
      // Single item — append to existing array rather than replace entire field
      updates._itemIndex = -1;
    }
    return this.updateProductStrategy(updates, planId);
  }

  async handleUpdateAudienceInsights(data, action = null, context = {}) {
    console.log("🎯 ProductStrategyCapability: Updating audience insights", data);
    
    const audienceInsights = data.audienceInsights || data.audience_insights || data.audience || data.content;
    const planId = context.planId || this.getters?.currentPlanId || (typeof this.getters?.getCurrentPlanId === 'function' ? this.getters.getCurrentPlanId() : null);
    
    if (!audienceInsights) {
      console.error("❌ No audience insights provided");
      return { success: false, message: "No audience insights provided" };
    }

    const updates = { audienceInsights };
    if (data._itemIndex !== undefined) {
      updates._itemIndex = data._itemIndex;
    } else if (!Array.isArray(audienceInsights) && audienceInsights.audience) {
      // Key-based: find and merge into the existing persona by audience name
      updates._itemKeyField = 'audience';
      updates._itemKey = audienceInsights.audience;
    } else if (!Array.isArray(audienceInsights)) {
      // No identifier — fall back to append
      updates._itemIndex = -1;
    }
    return this.updateProductStrategy(updates, planId);
  }

  async handleUpdateDesignHypotheses(data, action = null, context = {}) {
    console.log("🎯 ProductStrategyCapability: Updating design hypotheses", data);
    
    const designHypotheses = data.designHypotheses || data.design_hypotheses || data.hypotheses || data.content;
    const planId = context.planId || this.getters?.currentPlanId || (typeof this.getters?.getCurrentPlanId === 'function' ? this.getters.getCurrentPlanId() : null);
    
    if (!designHypotheses) {
      console.error("❌ No design hypotheses provided");
      return { success: false, message: "No design hypotheses provided" };
    }

    const updates = { designHypotheses };
    if (data._itemIndex !== undefined) updates._itemIndex = data._itemIndex;
    return this.updateProductStrategy(updates, planId);
  }

  async handleUpdateBriefDeconstruction(data, action = null, context = {}) {
    console.log("🎯 ProductStrategyCapability: Updating brief deconstruction", data);
    
    const briefDeconstruction = data.briefDeconstruction || data.brief_deconstruction || data.content;
    const planId = context.planId || this.getters?.currentPlanId || (typeof this.getters?.getCurrentPlanId === 'function' ? this.getters.getCurrentPlanId() : null);
    
    if (!briefDeconstruction) {
      console.error("❌ No brief deconstruction provided");
      return { success: false, message: "No brief deconstruction provided" };
    }

    return this.updateProductStrategy({ briefDeconstruction }, planId);
  }

  async handleUpdateInformationAssessment(data, action = null, context = {}) {
    console.log("🎯 ProductStrategyCapability: Updating information assessment", data);
    
    const informationAssessment = data.informationAssessment || data.information_assessment || data.content;
    const planId = context.planId || this.getters?.currentPlanId || (typeof this.getters?.getCurrentPlanId === 'function' ? this.getters.getCurrentPlanId() : null);
    
    if (!informationAssessment) {
      console.error("❌ No information assessment provided");
      return { success: false, message: "No information assessment provided" };
    }

    return this.updateProductStrategy({ informationAssessment }, planId);
  }

  async handleUpdateStrategicGuidance(data, action = null, context = {}) {
    console.log("🎯 ProductStrategyCapability: Updating strategic guidance", data);
    
    const strategicGuidance = data.strategicGuidance || data.strategic_guidance || data.content;
    const planId = context.planId || this.getters?.currentPlanId || (typeof this.getters?.getCurrentPlanId === 'function' ? this.getters.getCurrentPlanId() : null);
    
    if (!strategicGuidance) {
      console.error("❌ No strategic guidance provided");
      return { success: false, message: "No strategic guidance provided" };
    }

    return this.updateProductStrategy({ strategicGuidance }, planId);
  }

  async handleUpdateExperienceDesignPrinciples(data, action = null, context = {}) {
    console.log("🎯 ProductStrategyCapability: Updating experience design principles", data);
    
    let experienceDesignPrinciples = data.experienceDesignPrinciples || data.experience_design_principles || data.principles || data.content;
    const planId = context.planId || this.getters?.currentPlanId || (typeof this.getters?.getCurrentPlanId === 'function' ? this.getters.getCurrentPlanId() : null);
    
    if (!experienceDesignPrinciples) {
      const { _itemIndex, ...rest } = data;
      const keys = Object.keys(rest);
      if (keys.length > 0) {
        experienceDesignPrinciples = keys.length === 1 ? rest[keys[0]] : rest;
      }
    }

    if (!experienceDesignPrinciples) {
      console.error("❌ No experience design principles provided");
      return { success: false, message: "No experience design principles provided" };
    }

    const updates = { experienceDesignPrinciples };
    if (data._itemIndex !== undefined) updates._itemIndex = data._itemIndex;
    return this.updateProductStrategy(updates, planId);
  }

  async handleUpdateSuccessMetrics(data, action = null, context = {}) {
    console.log("🎯 ProductStrategyCapability: Updating success metrics", data);
    
    let successMetrics = data.successMetrics || data.success_metrics || data.metrics || data.content;
    const planId = context.planId || this.getters?.currentPlanId || (typeof this.getters?.getCurrentPlanId === 'function' ? this.getters.getCurrentPlanId() : null);
    
    if (!successMetrics) {
      const { _itemIndex, ...rest } = data;
      const keys = Object.keys(rest);
      if (keys.length > 0) {
        successMetrics = keys.length === 1 ? rest[keys[0]] : rest;
      }
    }

    if (!successMetrics) {
      console.error("❌ No success metrics provided");
      return { success: false, message: "No success metrics provided" };
    }

    const updates = { successMetrics };
    if (data._itemIndex !== undefined) {
      updates._itemIndex = data._itemIndex;
    } else if (!Array.isArray(successMetrics) && successMetrics && typeof successMetrics === 'object') {
      updates._itemIndex = -1;
    }
    return this.updateProductStrategy(updates, planId);
  }

  async handleUpdateUnmetNeeds(data, action = null, context = {}) {
    console.log("🎯 ProductStrategyCapability: Updating unmet needs", data);
    
    let unmetNeeds = data.unmetNeeds || data.unmet_needs || data.needs || data.content;
    const planId = context.planId || this.getters?.currentPlanId || (typeof this.getters?.getCurrentPlanId === 'function' ? this.getters.getCurrentPlanId() : null);
    
    if (!unmetNeeds) {
      const { _itemIndex, ...rest } = data;
      const keys = Object.keys(rest);
      if (keys.length > 0) {
        unmetNeeds = keys.length === 1 ? rest[keys[0]] : rest;
      }
    }

    if (!unmetNeeds) {
      console.error("❌ No unmet needs provided");
      return { success: false, message: "No unmet needs provided" };
    }

    const updates = { unmetNeeds };
    if (data._itemIndex !== undefined) updates._itemIndex = data._itemIndex;
    return this.updateProductStrategy(updates, planId);
  }

  async handleUpdateCompetitiveUxPositioning(data, action = null, context = {}) {
    console.log("🎯 ProductStrategyCapability: Updating competitive UX positioning", data);
    
    let competitiveUxPositioning = data.competitiveUxPositioning || data.competitive_ux_positioning || data.competitivePositioning;
    const planId = context.planId || this.getters?.currentPlanId || (typeof this.getters?.getCurrentPlanId === 'function' ? this.getters.getCurrentPlanId() : null);

    // Helper: extract bullet/numbered lines from text as key_differentiators objects.
    // Emits the canonical schema { differentiator, impact_on_users } used by the rest of
    // the system (ProductStrategyView, DB, AI prompt examples).
    // Handles "- text", "* text", "• text", and numbered "1. text" formats.
    const extractDifferentiators = (text) => {
      if (!text || typeof text !== 'string') return [];
      return text
        .split('\n')
        .map(l => l.trim())
        .filter(l => /^[-*•]/.test(l) || /^\d+\.\s/.test(l))
        .map(l => l.replace(/^[-*•]\s*/, '').replace(/^\d+\.\s*/, '').trim())
        .filter(Boolean)
        .map(differentiator => ({ differentiator, impact_on_users: '' }));
    };
    const parseStructuredPositioning = (text) => {
      if (!text || typeof text !== 'string') return null;
      try {
        const parsed = JSON.parse(text);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
          ? parsed
          : null;
      } catch {
        return null;
      }
    };
    
    if (!competitiveUxPositioning) {
      if (data.positioning_statement || data.key_differentiators) {
        competitiveUxPositioning = {
          positioning_statement: data.positioning_statement || '',
          key_differentiators: data.key_differentiators || [],
        };
      } else if (data.content && typeof data.content === 'object') {
        competitiveUxPositioning = data.content;
      } else if (data.content && typeof data.content === 'string') {
        const parsed = parseStructuredPositioning(data.content);
        if (parsed) {
          competitiveUxPositioning = parsed;
        } else {
          const diffs = extractDifferentiators(data.content);
          competitiveUxPositioning = {
            positioning_statement: data.content.split('\n')[0].replace(/^[-*•\d.]\s*/, '').trim(),
            key_differentiators: diffs,
          };
        }
      } else if (data.description && typeof data.description === 'string') {
        const parsed = parseStructuredPositioning(data.description);
        if (parsed) {
          competitiveUxPositioning = parsed;
        } else {
          const diffs = extractDifferentiators(data.description);
          competitiveUxPositioning = {
            positioning_statement: data.description.split('\n')[0].replace(/^[-*•\d.]\s*/, '').trim(),
            key_differentiators: diffs,
          };
        }
      }
    }

    if (competitiveUxPositioning && typeof competitiveUxPositioning === 'string') {
      // Server-side task results may expose structured JSON as a string for
      // transport. Preserve that structure instead of treating the JSON source
      // as the human-readable positioning statement.
      const parsedStructuredValue = parseStructuredPositioning(competitiveUxPositioning);

      if (parsedStructuredValue) {
        competitiveUxPositioning = parsedStructuredValue;
      } else {
        const diffs = extractDifferentiators(competitiveUxPositioning);
        competitiveUxPositioning = {
          positioning_statement: competitiveUxPositioning.split('\n')[0].replace(/^[-*•\d.]\s*/, '').trim(),
          key_differentiators: diffs,
        };
      }
    }

    // Normalize key_differentiators: if provided as a string (markdown bullets), parse it;
    // if null/undefined, default to []; if an object, wrap in an array.
    if (competitiveUxPositioning) {
      const kd = competitiveUxPositioning.key_differentiators;
      if (typeof kd === 'string') {
        competitiveUxPositioning.key_differentiators = extractDifferentiators(kd);
      } else if (!Array.isArray(kd)) {
        competitiveUxPositioning.key_differentiators = kd && typeof kd === 'object'
          ? [kd]
          : [];
      }
    }

    if (!competitiveUxPositioning) {
      console.error("❌ No competitive UX positioning provided");
      return { success: false, message: "No competitive UX positioning provided" };
    }

    return this.updateProductStrategy({ competitiveUxPositioning }, planId);
  }
}

module.exports = ProductStrategyCapability;
